import {HashRouter, Switch, Route} from 'react-router-dom';

import {Routing} from 'nutshell-core/routing';

import {ErrorState} from 'shells/error-state/error-state';
import {Modal} from 'shells/modal/modal';
import {LeftFrame, RightFrame} from 'shells/modal/step-modal/layout';
import {StepList} from 'shells/modal/step-modal/step-list';

import {appHistory} from '../../../history';

import {getNextPageForGettingStarted, getServiceTypeFromString} from '../../calls/helpers';
import {useGetSessionUser} from '../../core/use-get-session-user';

import {CallrailSetup} from './callrail-setup';
import {ChooseProvider} from './choose-provider';
import {ClickToCallConnected} from './click-to-call/click-to-call-connected';
import {ClickToCallSetup} from './click-to-call/click-to-call-setup';
import {ClickToCallValidateCode} from './click-to-call/click-to-call-validate-code';
import {ConfigureActivityTypes} from './configure-activity-types';
import {GettingStarted} from './getting-started';
import {InitialSync} from './initial-sync';
import {KixieSetup} from './kixie-setup';
import {SyncPreferences} from './sync-preferences';

type Props = {
    isOpen: boolean;
    onClose: () => void;
};

const PATH_STEPS = {
    '/connect-phone-integration/get-started': 0,
    '/connect-phone-integration/choose-provider': 1,
    '/connect-phone-integration/sync-preferences': 2,
    '/connect-phone-integration/initial-sync': 2,
    '/connect-phone-integration/configure-activity-types': 2,
    '/connect-phone-integration/done': 3,
    '/connect-phone-integration/click-to-call/setup': 2,
    '/connect-phone-integration/click-to-call/validate-code': 2,
    '/connect-phone-integration/click-to-call/connected': 2,
    '/connect-phone-integration/callrail-setup': 2,
    '/connect-phone-integration/kixie-setup': 2,
} as const;

const STEPS = ['1. Get started', '2. Pick your phone system', '3. Configure your integration'];

export function ConnectPhoneIntegrationModalRoot(props: Props) {
    const {user} = useGetSessionUser();

    const phoneIntegrations = user ? user.phoneIntegrations : [];
    const enabledPhoneIntegrations = phoneIntegrations.filter(
        (integration) => integration.isEnabledForUser
    );

    const canSyncPreviousCalls = enabledPhoneIntegrations.some(
        (integration) => integration.canSyncPreviousCalls
    );

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={props.onClose}
            canClickOutsideToClose={false}
            canEscapeToClose={false}
            maxHeight={640}
            minWidth={885}
            noPadding={true}
        >
            <div className='flex full-height'>
                <HashRouter>
                    <LeftFrame title='Set up your phone'>
                        <Route
                            render={({location}) => {
                                // @ts-expect-error
                                const activeStep = PATH_STEPS[location.pathname];

                                return <StepList activeStepIndex={activeStep} steps={STEPS} />;
                            }}
                        />
                    </LeftFrame>
                    <Switch>
                        <Route
                            path='/connect-phone-integration/get-started'
                            render={({history, location}) => {
                                const queryParams = Routing.deparam(location.search);
                                const integrationType = getServiceTypeFromString(
                                    queryParams.integrationType
                                );

                                const nextPage = getNextPageForGettingStarted(integrationType);

                                return (
                                    <GettingStarted
                                        integrationType={integrationType}
                                        user={user || null}
                                        locationSearch={location.search}
                                        nextPage={nextPage}
                                        history={history}
                                        onCancel={() => {
                                            props.onClose();
                                        }}
                                    />
                                );
                            }}
                        />
                        <Route
                            path='/connect-phone-integration/choose-provider'
                            render={({history, location}) => (
                                <ChooseProvider
                                    history={history}
                                    prevPage='/connect-phone-integration/get-started'
                                    search={location.search}
                                />
                            )}
                        />
                        <Route
                            path='/connect-phone-integration/callrail-setup'
                            render={({history}) => (
                                <CallrailSetup
                                    history={history}
                                    prevPage={`/connect-phone-integration/choose-provider`}
                                />
                            )}
                        />
                        <Route
                            path='/connect-phone-integration/kixie-setup'
                            render={({history}) => (
                                <KixieSetup
                                    history={history}
                                    prevPage={`/connect-phone-integration/choose-provider`}
                                />
                            )}
                        />
                        <Route
                            path='/connect-phone-integration/sync-preferences'
                            render={({history, location}) => {
                                const syncPreferencesNextPage = canSyncPreviousCalls
                                    ? `/connect-phone-integration/initial-sync${location.search}`
                                    : `/connect-phone-integration/configure-activity-types${location.search}`;

                                return (
                                    <SyncPreferences
                                        search={location.search}
                                        goBack={history.goBack}
                                        nextPage={syncPreferencesNextPage}
                                    />
                                );
                            }}
                        />
                        <Route
                            path='/connect-phone-integration/initial-sync'
                            render={({history, location}) => (
                                <InitialSync
                                    goBack={history.goBack}
                                    nextPage={`/connect-phone-integration/configure-activity-types${location.search}`}
                                />
                            )}
                        />
                        <Route
                            path='/connect-phone-integration/configure-activity-types'
                            render={({history, location}) => {
                                const queryParams = Routing.deparam(location.search);
                                const shouldSyncPastCalls =
                                    queryParams.shouldSyncPastCalls &&
                                    queryParams.shouldSyncPastCalls === '1';

                                return (
                                    <ConfigureActivityTypes
                                        goBack={history.goBack}
                                        nextPage={`/connect-phone-integration/done${location.search}`}
                                        history={history}
                                        shouldSyncPastCalls={shouldSyncPastCalls}
                                        search={location.search}
                                    />
                                );
                            }}
                        />
                        <Route
                            path='/connect-phone-integration/click-to-call/setup'
                            render={({history}) => {
                                return <ClickToCallSetup history={history} />;
                            }}
                        />
                        <Route
                            path='/connect-phone-integration/click-to-call/validate-code'
                            render={({history, location}) => {
                                const queryParams = Routing.deparam(location.search);
                                const name = queryParams.name;
                                const phoneNumber = queryParams.phoneNumber;
                                const verificationCode = queryParams.verificationCode;

                                if (!name || !phoneNumber || !verificationCode) {
                                    return (
                                        <RightFrame>
                                            <ErrorState errorMessage='Missing required information. Please retry setup.' />
                                        </RightFrame>
                                    );
                                }

                                return (
                                    <ClickToCallValidateCode
                                        name={name}
                                        phoneNumber={phoneNumber}
                                        verificationCode={verificationCode}
                                        history={history}
                                    />
                                );
                            }}
                        />
                        <Route
                            path='/connect-phone-integration/click-to-call/connected'
                            render={({history, location}) => {
                                const queryParams = Routing.deparam(location.search);
                                const name = queryParams.name;
                                const phoneNumber = queryParams.phoneNumber;

                                if (!name || !phoneNumber) {
                                    return (
                                        <RightFrame>
                                            <ErrorState errorMessage='Something went wrong. Please try again.' />
                                        </RightFrame>
                                    );
                                }

                                return (
                                    <ClickToCallConnected
                                        name={name}
                                        phoneNumber={phoneNumber}
                                        history={history}
                                    />
                                );
                            }}
                        />
                        <Route
                            path='/connect-phone-integration/done'
                            render={({location}) => {
                                const queryParams = Routing.deparam(location.search);
                                const integrationType = queryParams.integrationType;
                                const shouldAutolog = queryParams.shouldAutolog;

                                // if opened over a lead page, just return to that page
                                const currentPath = window.location.pathname;
                                const isOnLeadPage = currentPath.startsWith('/lead/');
                                if (isOnLeadPage) {
                                    props.onClose();

                                    return null;
                                }

                                let postOnboardingPage = null;
                                if (shouldAutolog === '1') {
                                    postOnboardingPage = '/sales/calls/logged-meetings';
                                } else if (integrationType === 'CLICKTOCALL') {
                                    postOnboardingPage = '/my-account/phone';
                                } else {
                                    postOnboardingPage = '/sales/calls/unlogged-meetings';
                                }

                                props.onClose();

                                appHistory.push(postOnboardingPage);

                                return null;
                            }}
                        />
                    </Switch>
                </HashRouter>
            </div>
        </Modal>
    );
}
